import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ProvisionTerminal } from "@/components/launch/ProvisionTerminal";
import { ObjectLink } from "@/components/OptionalLink";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { RefreshCw, ArrowUpRight, MoreVertical, Copy, Server } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { PROVIDER_ICON } from "@/components/icons/ProviderIcons";
import type { ProviderType } from "@/types";
import type { EventSourceMessageJournal } from "@/components/dashboard/createEventSource";

// Bouncy spring for the result panel (error message / launch note) expanding open. It animates the
// box height so the whole panel springs taller, then settles.
const RESULT_SPRING = { type: "spring" as const, stiffness: 300, damping: 17 };
// Quick, smooth collapse on removal — no bounce on the way out.
const RESULT_COLLAPSE = { duration: 0.2, ease: "easeIn" as const };

type DeployStatus = "running" | "completed" | "error" | "cancelling";

// Running labels for the live phase, surfaced in the terminal status badge.
const PHASE_RUNNING = ["Provisioning server", "Preparing host", "Deploying app", "Going live"];

// The note renders through markdown-to-jsx inside `.prose`, which forces code to weight 600 and
// wraps it in literal backticks. Force real monospace, normal weight, and strip those backticks.
// Fenced blocks keep the app's global `.markdown pre` styling.
const NOTE_PROSE =
  "leading-relaxed [&_code]:font-mono [&_code]:font-normal [&_code]:text-[0.9em] " +
  "[&_code]:break-all [&_code]:before:content-[''] [&_code]:after:content-['']";

// Infer how far the deploy has progressed from DollarDeploy's log vocabulary.
const phaseFromLogs = (logs: EventSourceMessageJournal[]): number => {
  let reached = 0;
  for (const log of logs) {
    const message = log.message.toLowerCase();
    if (
      /app is running|deployed successfully|nginx|ssl cert|certificate|https:\/\//.test(
        message
      )
    ) {
      reached = Math.max(reached, 3);
    } else if (
      /executing deploy|uploading file|docker|compose|container .* (creat|start|healthy)/.test(
        message
      )
    ) {
      reached = Math.max(reached, 2);
    } else if (
      /preparing host|executing prepare|cloud-init|updating env|adding service/.test(message)
    ) {
      reached = Math.max(reached, 1);
    }
  }
  return reached;
};

// "CPU.4V.16G" -> "4 vCPU · 16 GB RAM"; falls back to the raw value.
const formatSpecs = (size?: string): string | undefined => {
  if (!size) {
    return undefined;
  }
  const match = size.match(/(\d+)\s*V.*?(\d+)\s*G/i);
  return match ? `${match[1]} vCPU · ${match[2]} GB RAM` : size;
};

const OverflowMenu = ({ children }: { children: ReactNode }) => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger asChild>
      <Button size="icon" variant="ghost" className="size-8" aria-label="More actions">
        <MoreVertical className="size-4" strokeWidth={1.5} />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">{children}</DropdownMenuContent>
  </DropdownMenu>
);

export const DeployView = ({
  status,
  logs,
  appName,
  destinationType,
  destinationLabel,
  providerId,
  size,
  appUrl,
  appDetailsHref,
  errorMessage,
  postLaunchNote,
  busy,
  startedAt,
  endedAt,
  onCancel,
  onRetry,
  onRedeploy
}: {
  status: DeployStatus;
  logs: EventSourceMessageJournal[];
  appName: string;
  destinationType: string;
  destinationLabel: string;
  /** Provider whose logo leads the destination line; falls back to a generic server glyph. */
  providerId?: ProviderType;
  size?: string;
  appUrl?: string;
  appDetailsHref?: string;
  errorMessage?: ReactNode;
  postLaunchNote?: ReactNode;
  busy?: boolean;
  startedAt?: Date | string | number | null;
  endedAt?: Date | string | number | null;
  onCancel?: () => void;
  onRetry?: () => void;
  onRedeploy?: () => void;
}) => {
  const isActive = status === "running" || status === "cancelling";
  const specs = formatSpecs(size);
  const ProviderIcon = providerId ? PROVIDER_ICON[providerId] : undefined;
  const phaseLabel =
    status === "running"
      ? PHASE_RUNNING[Math.min(phaseFromLogs(logs), PHASE_RUNNING.length - 1)]
      : undefined;

  const [width, setWidth] = useState<number>(window.innerWidth);
  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const isMobile = width <= 768;

  const handleCopyUrl = () => {
    if (appUrl) {
      navigator.clipboard.writeText("https://" + appUrl);
    }
  };

  const visitButton =
    appUrl != null ? (
      <Button asChild size="sm">
        <a href={"https://" + appUrl} target="_blank" rel="noreferrer noopener">
          Visit
          <ArrowUpRight className="size-3.5" strokeWidth={1.5} />
        </a>
      </Button>
    ) : null;

  const appDetailsButton =
    appDetailsHref != null ? (
      // Thin the ObjectLink's app icon to match the 1.5-weight icons here, without touching the
      // shared component. CSS stroke-width overrides lucide's stroke-width="2" attribute.
      <Button asChild size="sm" variant="secondary" className="[&_svg]:[stroke-width:1.5]">
        <ObjectLink type="app" href={appDetailsHref}>
          App details
        </ObjectLink>
      </Button>
    ) : null;

  const copyUrlItem = appUrl ? (
    <DropdownMenuItem onClick={handleCopyUrl}>
      <Copy className="size-3.5" strokeWidth={1.5} />
      Copy URL
    </DropdownMenuItem>
  ) : null;

  return (
    <div className="flex flex-col gap-4">
      <ProvisionTerminal
        logs={logs}
        status={status}
        startedAt={startedAt}
        endedAt={endedAt}
        phaseLabel={phaseLabel}
      />

      <div className="rounded-xl border bg-card/50 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div
            className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-sm"
            title={`${appName} · ${destinationType}: ${destinationLabel}`}
          >
            {ProviderIcon ? (
              <ProviderIcon className="h-4 w-auto max-w-12 shrink-0" />
            ) : (
              <Server className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
            )}
            <span className="font-medium">{destinationLabel}</span>
            {specs && !isMobile ? (
              <span className="text-muted-foreground">· {specs}</span>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {isActive ? (
              <Button
                variant="outline"
                size="sm"
                onClick={onCancel}
                disabled={status === "cancelling"}
              >
                {status === "cancelling" ? "Cancelling…" : "Cancel launch"}
              </Button>
            ) : status === "error" ? (
              <>
                <Button size="sm" onClick={onRetry} disabled={busy}>
                  <RefreshCw className="size-3.5" strokeWidth={1.5} />
                  Retry launch
                </Button>
                {appDetailsButton}
                {appUrl ? (
                  <OverflowMenu>
                    {copyUrlItem}
                    <DropdownMenuItem asChild>
                      <a href={"https://" + appUrl} target="_blank" rel="noreferrer noopener">
                        <ArrowUpRight className="size-3.5" strokeWidth={1.5} />
                        Visit app
                      </a>
                    </DropdownMenuItem>
                  </OverflowMenu>
                ) : null}
              </>
            ) : status === "completed" ? (
              <>
                {visitButton}
                {appDetailsButton}
                {appUrl || onRedeploy ? (
                  <OverflowMenu>
                    {copyUrlItem}
                    {onRedeploy ? (
                      <DropdownMenuItem onClick={onRedeploy} disabled={busy}>
                        <RefreshCw className="size-3.5" strokeWidth={1.5} />
                        Redeploy
                      </DropdownMenuItem>
                    ) : null}
                  </OverflowMenu>
                ) : null}
              </>
            ) : null}
          </div>
        </div>

        <AnimatePresence initial={false}>
          {status === "error" && errorMessage ? (
            <motion.div
              key="error"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0, transition: RESULT_COLLAPSE }}
              transition={RESULT_SPRING}
              className="overflow-hidden"
            >
              <Separator />
              <div className="p-4 text-sm text-destructive">{errorMessage}</div>
            </motion.div>
          ) : status === "completed" && postLaunchNote ? (
            <motion.div
              key="note"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0, transition: RESULT_COLLAPSE }}
              transition={RESULT_SPRING}
              className="overflow-hidden"
            >
              <Separator />
              <div className={`p-4 text-sm ${NOTE_PROSE}`}>{postLaunchNote}</div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
};
